<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { EnhancementSourceLoadStatus } from '@/utils/editVehicleEnhancementSource'
import {
  type BulkEnhancementApplyRequest,
  type BulkEnhancementMode,
  type BulkEnhancementReviewResult,
  type BulkEnhancementReviewRow,
} from '@/utils/editVehicleBulkEnhancement'

const props = defineProps<{
  modelValue: boolean
  sourcePhase: EnhancementSourceLoadStatus
  resetToken: number
  reviewChanges: (mode: BulkEnhancementMode) => Promise<BulkEnhancementReviewResult>
  applyChanges: (request: BulkEnhancementApplyRequest) => Promise<void> | void
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  retry: []
}>()

const sourceItems = [{ title: 'Chrome', value: 'chrome' }]
const modeItems = [
  { title: 'Fill missing or invalid fields', value: 'fill_missing_or_invalid' },
  { title: 'Replace eligible fields', value: 'replace_eligible' },
]

const selectedSource = ref('chrome')
const selectedMode = ref<BulkEnhancementMode>('fill_missing_or_invalid')
const reviewRows = ref<BulkEnhancementReviewRow[]>([])
const reviewIdentity = ref('')
const reviewState = ref<'idle' | 'loading' | 'ready' | 'empty' | 'error'>('idle')
const applying = ref(false)

const selectedCount = computed(() => reviewRows.value.filter((row) => row.selected).length)

const canApply = computed(
  () =>
    props.modelValue &&
    reviewState.value === 'ready' &&
    selectedCount.value > 0 &&
    reviewIdentity.value.length > 0 &&
    !applying.value &&
    props.sourcePhase !== 'error',
)

function clearReview() {
  reviewRows.value = []
  reviewIdentity.value = ''
  reviewState.value = 'idle'
}

function resetPresentation() {
  selectedSource.value = 'chrome'
  selectedMode.value = 'fill_missing_or_invalid'
  applying.value = false
  clearReview()
}

function setSource(value: unknown) {
  const next = value === 'chrome' ? 'chrome' : selectedSource.value
  if (next === selectedSource.value) return
  selectedSource.value = next
  clearReview()
}

function setMode(value: unknown) {
  const next: BulkEnhancementMode =
    value === 'replace_eligible' ? 'replace_eligible' : 'fill_missing_or_invalid'
  if (next === selectedMode.value) return
  selectedMode.value = next
  clearReview()
}

function setRowSelected(field: string, selected: boolean) {
  reviewRows.value = reviewRows.value.map((row) =>
    row.field === field ? { ...row, selected } : row,
  )
}

function closeDialog() {
  emit('update:modelValue', false)
}

async function onReview() {
  if (props.sourcePhase === 'error') {
    reviewRows.value = []
    reviewIdentity.value = ''
    reviewState.value = 'error'
    return
  }

  const token = props.resetToken
  const mode = selectedMode.value
  const source = selectedSource.value
  reviewState.value = 'loading'
  try {
    const outcome = await props.reviewChanges(mode)
    if (
      !props.modelValue ||
      token !== props.resetToken ||
      mode !== selectedMode.value ||
      source !== selectedSource.value
    ) {
      return
    }
    if (outcome.status === 'error') {
      reviewRows.value = []
      reviewIdentity.value = ''
      reviewState.value = 'error'
      return
    }
    reviewRows.value = outcome.rows.map((row) => ({ ...row, selected: true }))
    reviewIdentity.value = outcome.identity
    reviewState.value = reviewRows.value.length > 0 ? 'ready' : 'empty'
  } catch {
    if (
      !props.modelValue ||
      token !== props.resetToken ||
      mode !== selectedMode.value ||
      source !== selectedSource.value
    ) {
      return
    }
    reviewRows.value = []
    reviewIdentity.value = ''
    reviewState.value = 'error'
  }
}

async function onApply() {
  if (!canApply.value) return
  const token = props.resetToken
  const request: BulkEnhancementApplyRequest = {
    fields: reviewRows.value.filter((row) => row.selected).map((row) => row.field),
    identity: reviewIdentity.value,
  }
  applying.value = true
  try {
    await props.applyChanges(request)
  } finally {
    if (props.modelValue && token === props.resetToken) applying.value = false
  }
}

function onRetry() {
  clearReview()
  emit('retry')
}

watch(
  () => props.resetToken,
  () => {
    resetPresentation()
  },
)

watch(
  () => props.modelValue,
  (open, wasOpen) => {
    if (wasOpen && !open) resetPresentation()
  },
)
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="760"
    persistent
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card v-if="modelValue" data-testid="enhance-vehicle-data-dialog">
      <v-card-title class="text-h6">Enhance data</v-card-title>
      <v-card-text>
        <v-select
          :model-value="selectedSource"
          :items="sourceItems"
          item-title="title"
          item-value="value"
          label="Source"
          variant="outlined"
          density="comfortable"
          hide-details
          class="mb-4"
          data-testid="enhance-vehicle-data-source"
          @update:model-value="setSource"
        />

        <v-select
          :model-value="selectedMode"
          :items="modeItems"
          item-title="title"
          item-value="value"
          label="Mode"
          variant="outlined"
          density="comfortable"
          hide-details
          class="mb-4"
          data-testid="enhance-vehicle-data-mode"
          @update:model-value="setMode"
        />

        <div
          v-if="sourcePhase === 'loading' || reviewState === 'loading'"
          class="text-body-2 text-medium-emphasis mb-4"
          data-testid="enhance-vehicle-data-loading"
        >
          Chrome loading
        </div>

        <div
          v-if="sourcePhase === 'error' || reviewState === 'error'"
          class="mb-4"
          data-testid="enhance-vehicle-data-error"
        >
          <div class="text-body-2 mb-2">Chrome could not be loaded.</div>
          <v-btn
            v-if="sourcePhase === 'error'"
            type="button"
            variant="text"
            size="small"
            class="text-none px-0"
            data-testid="enhance-vehicle-data-retry"
            @click="onRetry"
          >
            Retry
          </v-btn>
        </div>

        <div
          v-else-if="reviewState === 'empty'"
          class="text-body-2 text-medium-emphasis mb-4"
          data-testid="enhance-vehicle-data-empty"
        >
          Chrome does not have any applicable changes for this mode.
        </div>

        <div
          v-else-if="reviewState === 'ready'"
          class="d-flex flex-column ga-3"
          data-testid="enhance-vehicle-data-rows"
        >
          <div
            v-for="row in reviewRows"
            :key="row.field"
            class="enhance-vehicle-data-row"
            :data-testid="`enhance-vehicle-data-row-${row.field}`"
          >
            <v-checkbox
              :model-value="row.selected"
              :label="row.label"
              density="compact"
              hide-details
              :data-testid="`enhance-vehicle-data-select-${row.field}`"
              @update:model-value="setRowSelected(row.field, Boolean($event))"
            />
            <div class="text-body-2 text-medium-emphasis">
              <span :data-testid="`enhance-vehicle-data-current-${row.field}`">{{
                row.currentDisplay
              }}</span>
              <span aria-hidden="true"> → </span>
              <span :data-testid="`enhance-vehicle-data-chrome-${row.field}`">{{
                row.chromeDisplay
              }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-btn type="button" variant="text" class="text-none" @click="closeDialog">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          type="button"
          variant="outlined"
          class="text-none"
          data-testid="enhance-vehicle-data-review"
          :loading="reviewState === 'loading'"
          :disabled="reviewState === 'loading' || applying"
          @click="onReview"
        >
          Review Changes
        </v-btn>
        <v-btn
          type="button"
          color="primary"
          class="text-none"
          data-testid="enhance-vehicle-data-apply"
          :disabled="!canApply"
          :loading="applying"
          @click="onApply"
        >
          Apply Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.enhance-vehicle-data-row {
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}
</style>
